import { useEffect, useMemo, useState } from 'react'
import { api } from '@/api'
import type { UsageSummary } from '@/api/types'
import { HBar, MiniBars } from '@/components/MiniBars'
import { Badge, Button, Card, CardBody, CardHead, Empty, Input, Stat } from '@/components/ui'
import { modelLabel } from '@/config/catalog'
import { saveText } from '@/lib/download'

type Tab = 'overview' | 'models' | 'records'

export function UsageAudit() {
  const [data, setData] = useState<UsageSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState<Tab>('overview')
  const [statusFilter, setStatusFilter] = useState<'all' | 'success' | 'failed'>('all')
  const [q, setQ] = useState('')

  useEffect(() => {
    api
      .usage()
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false))
  }, [])

  const records = useMemo(() => {
    if (!data) return []
    let l = data.records
    if (statusFilter !== 'all') l = l.filter((r) => r.status === statusFilter)
    if (q.trim()) {
      const kw = q.trim().toLowerCase()
      l = l.filter(
        (r) =>
          r.appName.toLowerCase().includes(kw) ||
          modelLabel(r.model).toLowerCase().includes(kw) ||
          r.model.toLowerCase().includes(kw),
      )
    }
    return l
  }, [data, statusFilter, q])

  const exportCsv = () => {
    if (!data) return
    const head = 'time,app,page,model,status,coins,cost,outputs'
    const rows = data.records.map((r) =>
      [r.createdAt, r.appName, r.sourcePage, r.model, r.status, r.coins, r.cost, r.outputs].join(','),
    )
    saveText(`usage-${Date.now()}.csv`, [head, ...rows].join('\n'), 'text/csv')
  }

  if (loading) {
    return (
      <div className="page">
        <Card>
          <CardBody>
            <div className="row text-sm muted" style={{ gap: 8 }}>
              <span className="spinner" /> 正在拉取用量数据…
            </div>
          </CardBody>
        </Card>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="page">
        <Card>
          <CardBody>
            <Empty
              icon="alert"
              title="用量接口不可用"
              text="当前数据层拿不到用量数据。若已切到 http 模式，请确认后端已实现 GET /api/usage/summary。"
            />
          </CardBody>
        </Card>
      </div>
    )
  }

  const maxModelCalls = Math.max(...data.byModel.map((m) => m.calls), 1)
  const maxPageCalls = Math.max(...data.byPage.map((p) => p.calls), 1)

  return (
    <div className="page page--wide">
      <div className="stat-grid" style={{ marginBottom: 24 }}>
        <Stat label="总调用次数" value={data.totalCalls.toLocaleString()} delta="近 14 天" icon="activity" />
        <Stat label="总消耗" value={`¥${data.totalCost.toFixed(2)}`} delta={`≈ ${data.totalCoins.toFixed(0)} 积分`} icon="coin" />
        <Stat label="24 小时调用" value={data.recent24h} icon="bolt" />
        <Stat
          label="成功率"
          value={`${(data.successRate * 100).toFixed(1)}%`}
          deltaTone="up"
          delta="失败不计费"
          icon="check"
        />
      </div>

      <div className="row row--between" style={{ marginBottom: 16 }}>
        <div className="row" style={{ gap: 6 }}>
          {(
            [
              ['overview', '总览'],
              ['models', '按模型'],
              ['records', '调用明细'],
            ] as [Tab, string][]
          ).map(([k, label]) => (
            <button key={k} className="chip" data-active={tab === k} type="button" onClick={() => setTab(k)}>
              {label}
            </button>
          ))}
        </div>
        <Button variant="outline" size="sm" icon="download" onClick={exportCsv}>
          导出 CSV
        </Button>
      </div>

      {tab === 'overview' ? (
        <div className="layout-2">
          <Card>
            <CardHead title="每日调用" hint="近 14 天" icon="chart" />
            <CardBody>
              <MiniBars data={data.byDay.map((d) => ({ label: d.day, value: d.calls }))} height={140} />
              <div className="bars-labels">
                <span>{data.byDay[0]?.day}</span>
                <span>{data.byDay[Math.floor(data.byDay.length / 2)]?.day}</span>
                <span>{data.byDay[data.byDay.length - 1]?.day}</span>
              </div>
              <div style={{ marginTop: 20 }}>
                <div className="text-xs muted" style={{ marginBottom: 8 }}>
                  每日消耗金额（元）
                </div>
                <MiniBars
                  data={data.byDay.map((d) => ({ label: d.day, value: d.cost }))}
                  height={70}
                  formatValue={(v) => `¥${v.toFixed(2)}`}
                />
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHead title="按页面用量" hint="哪个功能最费钱，一眼看出来" icon="grid" />
            <CardBody>
              <div className="stack stack--sm">
                {data.byPage.map((p) => (
                  <div key={p.page} className="row" style={{ gap: 10 }}>
                    <span className="text-xs" style={{ width: 96, flex: '0 0 auto' }}>
                      {p.label}
                    </span>
                    <div className="grow">
                      <HBar value={p.calls} max={maxPageCalls} />
                    </div>
                    <span className="text-xs muted num" style={{ width: 46, textAlign: 'right' }}>
                      {p.calls}
                    </span>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>
        </div>
      ) : null}

      {tab === 'models' ? (
        <Card>
          <CardHead title="按模型统计" hint="决定后续该砍哪个模型、加哪个供应商" icon="bolt" />
          <CardBody>
            <div className="stack stack--sm">
              {data.byModel.map((m) => (
                <div key={m.model} className="row" style={{ gap: 12 }}>
                  <span className="text-sm" style={{ width: 190, flex: '0 0 auto' }} title={m.model}>
                    {m.label}
                  </span>
                  <div className="grow">
                    <HBar value={m.calls} max={maxModelCalls} />
                  </div>
                  <span className="text-xs muted num" style={{ width: 54, textAlign: 'right' }}>
                    {m.calls} 次
                  </span>
                  <span className="text-xs num" style={{ width: 62, textAlign: 'right', fontWeight: 600 }}>
                    ¥{m.cost.toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
            <div className="text-xs muted" style={{ marginTop: 16 }}>
              单次均价 ≈ ¥{(data.totalCost / Math.max(data.totalCalls, 1)).toFixed(3)}，可作为定价与成本核算基线。
            </div>
          </CardBody>
        </Card>
      ) : null}

      {tab === 'records' ? (
        <Card>
          <CardHead
            title="调用明细"
            hint={`共 ${data.records.length} 条`}
            icon="activity"
            right={
              <>
                <div className="row" style={{ gap: 6 }}>
                  {(
                    [
                      ['all', '全部'],
                      ['success', '成功'],
                      ['failed', '失败'],
                    ] as ['all' | 'success' | 'failed', string][]
                  ).map(([k, label]) => (
                    <button
                      key={k}
                      className="chip"
                      data-active={statusFilter === k}
                      type="button"
                      onClick={() => setStatusFilter(k)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="搜索功能或模型" style={{ width: 170 }} />
              </>
            }
          />
          <CardBody tight>
            {!records.length ? (
              <Empty icon="activity" title="没有匹配的记录" text="换个筛选条件或关键词。" />
            ) : (
              <div className="table__wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>时间</th>
                      <th>功能</th>
                      <th>模型</th>
                      <th>状态</th>
                      <th style={{ textAlign: 'right' }}>积分</th>
                      <th style={{ textAlign: 'right' }}>金额</th>
                      <th style={{ textAlign: 'right' }}>产出</th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.slice(0, 60).map((r) => (
                      <tr key={r.id}>
                        <td className="nowrap text-xs muted">
                          {new Date(r.createdAt).toLocaleString('zh-CN', {
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </td>
                        <td className="text-sm">{r.appName}</td>
                        <td className="text-xs" title={r.model}>
                          {modelLabel(r.model)}
                        </td>
                        <td>
                          <Badge tone={r.status === 'success' ? 'success' : 'danger'}>
                            {r.status === 'success' ? '成功' : '失败'}
                          </Badge>
                        </td>
                        <td className="num text-xs">{r.coins.toFixed(1)}</td>
                        <td className="num text-xs">{r.cost ? `¥${r.cost.toFixed(3)}` : '—'}</td>
                        <td className="num text-xs">{r.outputs || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>
      ) : null}
    </div>
  )
}
